import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, input, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { HeaderComponent as VpHeaderComponent } from '@valk-nx/compositions/ui-header/src/lib/header.component';
import {
	CtaGroup,
	CtaItem,
	HeaderMenuItem,
	MetaMenu,
	TranslatedSlug,
} from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import { RouteHelper } from '@valk-nx/router-store/router.helper';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import {
	Button,
	DropdownButton,
	Link,
	MenuItem,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

@Component({
	selector: 'sb-header',
	templateUrl: './header.html',
	imports: [VpHeaderComponent, StoryblokDirective],
})
export class HeaderComponent extends StoryblokRootDirective {
	router = inject(Router);
	private readonly platformId = inject(PLATFORM_ID);
	readonly hotelStore = inject(HotelStore);

	readonly title = input<string>('');
	readonly menuItems = input<MenuItem[]>([]);
	readonly accountLink = input<Link>();
	readonly contactLink = input<Link>();
	readonly callToActionTitle = input<string>('');
	readonly callToActionLink = input<Link>();
	readonly translatedSlugs = input<TranslatedSlug[]>([]);
	readonly menu = input<HeaderMenuItem[]>([]);
	readonly hideMenu = input<boolean>(false);
	readonly cta = input<Button[] | DropdownButton[]>();

	readonly resolvedCta = computed<CtaItem | CtaGroup | undefined>(() => {
		const cta = this.cta();
		if (!cta?.[0]) return undefined;
		if (cta[0].component === 'dropdownButton') {
			const dropdown = cta[0] as DropdownButton;
			return {
				title: dropdown.title,
				items: dropdown.items.map((item) => ({
					title: item.text,
					url: item.url.cached_url,
					target: item.url.target,
				})),
			};
		}
		const button = cta[0] as Button;
		return {
			title: button.text,
			url: button.link.cached_url,
			target: button.link.target,
		};
	});

	readonly headerMenu = computed(() =>
		this.constructMenuItems(this.menuItems()),
	);

	readonly metaMenu = computed(() => this.constructMetaMenu());

	showSearch = false;

	constructor() {
		super();
	}

	constructMenuItems(menuItems: MenuItem[]): HeaderMenuItem[] {
		return menuItems.map((item: MenuItem) => {
			return {
				title: item.title,
				url: item.link.cached_url,
				target: item.link.target ?? '_self',
			};
		});
	}

	constructMetaMenu(): MetaMenu {
		const translatedSlugs = this.translatedSlugs();
		const accountLink = this.accountLink();
		const contactLink = this.contactLink();
		const callToActionLink = this.callToActionLink();
		const callToActionTitle = this.callToActionTitle();

		const slugs = translatedSlugs?.map((slug) => ({
			...slug,
			path: slug.path.endsWith('home')
				? slug.path.replace(/(\/?)home$/, '')
				: slug.path,
		}));
		const selectedSlug = slugs?.find((slug) => slug.selected);

		return {
			cta: this.resolvedCta() ?? {
				title: callToActionTitle,
				url: callToActionLink?.cached_url ?? '',
			},
			accountLink: {
				title: '',
				url: accountLink
					? (StoryblokHelper.getUrlFromStoryblokLink(
							accountLink,
						) as string)
					: '',
				target: accountLink?.target,
			},
			contactLink: {
				title: '',
				url: contactLink
					? (StoryblokHelper.getUrlFromStoryblokLink(
							contactLink,
						) as string)
					: '',
				target: contactLink?.target,
			},
			showSearch: this.showSearch,
			languages: {
				selectedLanguageOption: selectedSlug
					? selectedSlug
					: slugs?.[0],
				options: slugs?.map((slug) => {
					return {
						value: slug.path,
						label: slug.lang.toUpperCase(),
						selectLabel: slug.name,
					};
				}),
			},
		};
	}

	onLanguageSwitch(url: string): void {
		const urlSegments = this.getCurrentUrl().split('?');
		if (urlSegments.length > 1) {
			RouteHelper.redirectToExternalUrl(`${url}?${urlSegments[1]}`);
			return;
		}
		RouteHelper.redirectToExternalUrl(url);
	}

	getCurrentUrl(): string {
		if (isPlatformBrowser(this.platformId)) {
			return window.location.href;
		}
		return '';
	}
}
